import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';

import { getClaimAction } from 'store/actions/propertyActions';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';

import { ClaimTable } from 'components/Tables/ClaimTable';
import { Button, DatePickerInput, Dropdown, Loading } from '@leapeasy/ui-kit';
import { getClaim } from 'store/sagas/APIs/property';
import { useListQueryState } from 'hooks/useListQueryState';
import { usePaginatedListFetch } from 'hooks/usePaginatedListFetch';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { fetchAllPaginated } from 'utils/exportPagination';
import { ApplicationListActiveFilters } from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  buildClaimListFilterChips,
  parseClaimListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  DEFAULT_CLAIM_LIST_EXTRA_FILTERS,
  areArraysEqual,
  areDateRangesEqual,
  normalizeArrayFilter,
  normalizeDateRangeFilter,
  parseClaimsExtraFilters,
  serializeClaimsFilters,
} from 'utils/claimListQueryFilters';
import { getBreadcrumbParentsFromState } from 'utils/breadcrumbNavigation';
import {
  CLAIM_LIST_APP_TYPE_FILTER_OPTIONS,
  CLAIM_FILE_STATUS_FILTER_OPTIONS,
} from 'data/constants/common_constants';

const EMPTY_CLAIM_LIST_FILTER = {
  searchText: '',
  options: {},
  selectedFileStatus: [],
  selectedAppType: [],
  selectedLeaseDate: {
    startDate: null,
    endDate: null,
  },
};

export const Claims = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Claims..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_CLAIM_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseClaimsExtraFilters,
    serializeExtraFilters: serializeClaimsFilters,
  });
  const [selectedFileStatus, setSelectedFileStatus] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedFileStatus),
  );
  const [selectedAppType, setSelectedAppType] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDate, setSelectedLeaseDate] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedFileStatusDraft, setSelectedFileStatusDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedFileStatus),
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDateDraft, setSelectedLeaseDateDraft] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedFileStatus = normalizeArrayFilter(extraFilters.selectedFileStatus);
    const nextSelectedAppType = normalizeArrayFilter(extraFilters.selectedAppType);
    const nextSelectedLeaseDate = normalizeDateRangeFilter(extraFilters.selectedLeaseDate);
    setSelectedFileStatus((prev) =>
      areArraysEqual(prev, nextSelectedFileStatus) ? prev : nextSelectedFileStatus,
    );
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDate((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedFileStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedFileStatus) ? prev : nextSelectedFileStatus,
    );
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDateDraft((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
  }, [extraFilters]);

  const claims = useSelector((state) => state.getIn(['property', 'claim']));
  const claimsJSON = claims?.toJS?.() ?? null;
  const claimListError = claimsJSON?.claimError;

  const listQuery = useMemo(
    () => ({
      filter: {
        ...filter,
        selectedFileStatus,
        selectedAppType,
        selectedLeaseDate,
      },
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
    }),
    [
      filter,
      selectedFileStatus,
      selectedAppType,
      selectedLeaseDate,
      pagination.pageNumber,
      pagination.rowsPerPage,
      sortOptions,
    ],
  );

  usePaginatedListFetch({
    action: getClaimAction,
    payload: listQuery,
  });

  const refetchList = useCallback(() => {
    dispatch(getClaimAction(listQuery));
  }, [dispatch, listQuery]);

  const fetchFullExportDatasetTotal = useCallback(async () => {
    const response = await getClaim({
      filter: EMPTY_CLAIM_LIST_FILTER,
      offset: 0,
      limit: 1,
      sortOptions,
    });
    return response?.total != null ? Number(response.total) : undefined;
  }, [sortOptions]);

  const {
    fullExportDatasetTotal,
    isLoadingFullExportTotal,
    loadFullExportDatasetTotal,
    resetFullExportDatasetTotal,
  } = useLazyExportDatasetTotal(fetchFullExportDatasetTotal);

  useEffect(() => {
    resetFullExportDatasetTotal();
  }, [sortOptions, resetFullExportDatasetTotal]);

  const onClickClaim = useCallback(
    (claim) => {
      if (!claim?.id) {
        dispatch(failedNotif('Claim detail ID is missing.'));
        return;
      }

      dispatch(
        openDetails({
          type: 'claim',
          data: claim,
        }),
      );
      navigate(`/property/claims/${claim.id}/detail`, {
        state: {
          breadcrumbParents: getBreadcrumbParentsFromState(location.state),
        },
      });
    },
    [dispatch, navigate, location.state],
  );

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(claimsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getClaim({
            filter: {
              ...filter,
              selectedFileStatus,
              selectedAppType,
              selectedLeaseDate,
            },
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export filtered claims right now.');
    }
  }, [
    claimsJSON?.total,
    filter,
    selectedFileStatus,
    selectedAppType,
    selectedLeaseDate,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(await loadFullExportDatasetTotal()) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getClaim({
            filter: EMPTY_CLAIM_LIST_FILTER,
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all claims right now.');
    }
  }, [fullExportDatasetTotal, loadFullExportDatasetTotal, sortOptions]);

  const applyFunc = useCallback(() => {
    setSelectedFileStatus(selectedFileStatusDraft);
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedLeaseDate(selectedLeaseDateDraft);
    setExtraFilters({
      selectedFileStatus: selectedFileStatusDraft,
      selectedAppType: selectedAppTypeDraft,
      selectedLeaseDate: selectedLeaseDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedFileStatusDraft,
    selectedAppTypeDraft,
    selectedLeaseDateDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedFileStatus([]);
    setSelectedAppType([]);
    setSelectedLeaseDate({
      startDate: null,
      endDate: null,
    });
    setSelectedFileStatusDraft([]);
    setSelectedAppTypeDraft([]);
    setSelectedLeaseDateDraft({
      startDate: null,
      endDate: null,
    });
    setExtraFilters({
      selectedFileStatus: [],
      selectedAppType: [],
      selectedLeaseDate: {
        startDate: null,
        endDate: null,
      },
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const APPTYPE = CLAIM_LIST_APP_TYPE_FILTER_OPTIONS;

  const CLAIM_FILE_STATUS = CLAIM_FILE_STATUS_FILTER_OPTIONS;

  const listFilterChips = useMemo(
    () =>
      buildClaimListFilterChips({
        selectedFileStatus,
        selectedAppType,
        selectedLeaseDate,
        fileStatusOptions: CLAIM_FILE_STATUS,
        appTypeOptions: APPTYPE,
      }),
    [selectedFileStatus, selectedAppType, selectedLeaseDate, CLAIM_FILE_STATUS, APPTYPE],
  );

  const handleRemoveClaimListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseClaimListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'created') {
        const cleared = { startDate: null, endDate: null };
        setSelectedLeaseDate(cleared);
        setSelectedLeaseDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'fileStatus') {
        const next = selectedFileStatus.filter((s) => s !== parsed.value);
        setSelectedFileStatus(next);
        setSelectedFileStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedFileStatus: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedFileStatus, selectedAppType, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveClaimListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveClaimListFilterChip, resetFunc]);

  const claimTotal = Number(claimsJSON?.total) || 0;
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: claimTotal,
    isLoading: Boolean(claimsJSON?.isLoading),
  });

  return (
    <DashboardLayoutContainer>
      {claimsJSON ? (
        <React.Fragment>
          <CustomBox>
            {claimListError && !claimsJSON.isLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
                <GlobalUXState variant="apiError" compact description={claimListError} />
                <Button variant="primary" size="medium" onClick={refetchList}>
                  Retry
                </Button>
              </Box>
            ) : null}
            <ClaimTable
              claims={claimsJSON.data ?? []}
              loading={claimsJSON.isLoading}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={loadFullExportDatasetTotal}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              dropFilterActiveSummary={listFilterActiveSummary}
              dropFilterActiveCount={listFilterChips.length}
              filter={filter}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedFileStatusDraft(e.target.value);
                      }}
                      options={[...CLAIM_FILE_STATUS]}
                      value={selectedFileStatusDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder={'Select item'}
                      placeholderFlag={true}
                      label={'File Status'}
                      style={{
                        width: '100%',
                      }}
                    />
                    <Dropdown
                      onChange={(e) => {
                        setSelectedAppTypeDraft(e.target.value);
                      }}
                      options={[...APPTYPE]}
                      value={selectedAppTypeDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="Select item"
                      placeholderFlag={true}
                      label="Application Type"
                      style={{
                        width: '100%',
                      }}
                    />

                    <RangeDatePickerContainer>
                      <DatePickerInput
                        label={'Created From'}
                        size="small"
                        value={selectedLeaseDateDraft.startDate}
                        maxDate={selectedLeaseDateDraft.endDate}
                        onChange={(date) => {
                          setSelectedLeaseDateDraft((prev) => ({
                            ...prev,
                            startDate: date.target.value,
                          }));
                        }}
                      />
                      <DatePickerInput
                        label={'Created To'}
                        size="small"
                        value={selectedLeaseDateDraft.endDate}
                        minDate={selectedLeaseDateDraft.startDate}
                        onChange={(date) => {
                          setSelectedLeaseDateDraft((prev) => ({
                            ...prev,
                            endDate: date.target.value,
                          }));
                        }}
                      />
                    </RangeDatePickerContainer>
                  </DropFilterContainer>
                ),
                applyFunc: applyFunc,
                resetFunc: resetFunc,
              }}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: claimTotal,
                totalPages: Math.ceil(claimTotal / pagination.rowsPerPage) || 0,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickClaim={onClickClaim}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const RangeDatePickerContainer = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  * {
    box-sizing: border-box;
  }
  & > div {
    width: 50% !important;
  }

  .ui-kit-input-body {
    width: 60%;
  }
`;
